/**
 * LeaderboardService - ELO-based ranking and leaderboard system
 * Handles player stats, rankings, and ELO calculations
 */

import { supabase, isSupabaseConfigured, supabaseRawFetch, getAccessToken, getDisplayUsername, getTimeAgoDate, cachedFetch, clearCache } from '../lib/supabase';
import { authService } from '../auth';

// ========== Types ==========

export interface PlayerStats {
  userId: string;
  eloRating: number;
  gamesPlayed: number;
  wins: number;
  losses: number;
  draws: number;
  winStreak: number;
  bestWinStreak: number;
  lastPlayedAt: string | null;
}

export interface LeaderboardEntry extends PlayerStats {
  rank: number;
  username: string;
  displayName: string | null;
  avatarUrl: string | null;
}

export interface EloHistoryEntry {
  id: string;
  userId: string;
  gameId: string | null;
  oldElo: number;
  newElo: number;
  change: number;
  createdAt: string;
}

export interface EloChange {
  winnerChange: number;
  loserChange: number;
  winnerNewElo: number;
  loserNewElo: number;
}

export type RankTier = 'Bronze' | 'Silver' | 'Gold' | 'Platinum' | 'Diamond';

export interface RankBadge {
  tier: RankTier;
  color: string;
  minElo: number;
  maxElo: number | null;
}

export type TimeFilter = 'all' | 'month' | 'week';

// ========== Constants ==========

const RANK_TIERS: RankBadge[] = [
  { tier: 'Bronze', color: '#cd7f32', minElo: 0, maxElo: 1199 },
  { tier: 'Silver', color: '#a8a8a8', minElo: 1200, maxElo: 1399 },
  { tier: 'Gold', color: '#ffd700', minElo: 1400, maxElo: 1599 },
  { tier: 'Platinum', color: '#00d9d9', minElo: 1600, maxElo: 1799 },
  { tier: 'Diamond', color: '#4a90d9', minElo: 1800, maxElo: null },
];

const DEFAULT_ELO = 1200;
const K_FACTOR = 32;

// ========== Service ==========

export class LeaderboardService {
  /**
   * Get leaderboard with top players sorted by ELO
   * Uses caching to reduce repeated fetches (30s TTL)
   */
  async getLeaderboard(
    limit: number = 25,
    offset: number = 0,
    timeFilter: TimeFilter = 'all'
  ): Promise<{ entries: LeaderboardEntry[]; total: number }> {
    if (!isSupabaseConfigured()) {
      return { entries: [], total: 0 };
    }

    const cacheKey = `leaderboard:${limit}:${offset}:${timeFilter}`;
    
    return cachedFetch(cacheKey, async () => {
      try {
        // Build query parameters for raw fetch
        let filters = 'games_played=gt.0';
        
        // Apply time filter using shared utility
        const timeAgoDate = getTimeAgoDate(timeFilter);
        if (timeAgoDate) {
          filters += `&last_played_at=gte.${timeAgoDate.toISOString()}`;
        }

        const endpoint = `player_stats?${filters}&select=user_id,elo_rating,games_played,wins,losses,draws,win_streak,best_win_streak,last_played_at,profiles(username,display_name,avatar_url)&order=elo_rating.desc&offset=${offset}&limit=${limit}`;
        
        const accessToken = await getAccessToken();
        const { data, error } = await supabaseRawFetch<Array<any>>(endpoint, {
          accessToken: accessToken || undefined,
          timeoutMs: 8000,
        });

        if (error) {
          console.error('Error fetching leaderboard:', error);
          return { entries: [], total: 0 };
        }

        const entries: LeaderboardEntry[] = (data || []).map((row: any, index: number) => ({
          rank: offset + index + 1,
          userId: row.user_id,
          eloRating: row.elo_rating,
          gamesPlayed: row.games_played,
          wins: row.wins,
          losses: row.losses,
          draws: row.draws,
          winStreak: row.win_streak,
          bestWinStreak: row.best_win_streak,
          lastPlayedAt: row.last_played_at,
          username: getDisplayUsername(row.profiles, row.user_id),
          displayName: row.profiles?.display_name || null,
          avatarUrl: row.profiles?.avatar_url || null,
        }));

        return { entries, total: entries.length };
      } catch (error) {
        console.error('Error in getLeaderboard:', error);
        return { entries: [], total: 0 };
      }
    }, 30000); // 30 second cache
  }

  /**
   * Clear leaderboard cache (call after game results are reported)
   */
  clearLeaderboardCache(): void {
    clearCache('leaderboard:25:0:all');
    clearCache('leaderboard:25:0:month');
    clearCache('leaderboard:25:0:week');
  }

  /**
   * Get a player's rank position on the leaderboard
   */
  async getPlayerRank(userId: string): Promise<number | null> {
    if (!isSupabaseConfigured() || !supabase) {
      return null;
    }

    try {
      // Get the player's ELO
      const { data: playerStats, error: statsError } = await supabase
        .from('player_stats')
        .select('elo_rating')
        .eq('user_id', userId)
        .single();

      if (statsError || !playerStats) {
        return null;
      }

      // Count players with higher ELO
      const { count, error: countError } = await supabase
        .from('player_stats')
        .select('*', { count: 'exact', head: true })
        .gt('elo_rating', playerStats.elo_rating)
        .gt('games_played', 0);

      if (countError) {
        console.error('Error getting player rank:', countError);
        return null;
      }

      return (count || 0) + 1;
    } catch (error) {
      console.error('Error in getPlayerRank:', error);
      return null;
    }
  }

  /**
   * Get full stats for a specific player
   */
  async getPlayerStats(userId: string): Promise<PlayerStats | null> {
    if (!isSupabaseConfigured() || !supabase) {
      return null;
    }

    try {
      const { data, error } = await supabase
        .from('player_stats')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          // No stats found, return defaults
          return {
            userId,
            eloRating: DEFAULT_ELO,
            gamesPlayed: 0,
            wins: 0,
            losses: 0,
            draws: 0,
            winStreak: 0,
            bestWinStreak: 0,
            lastPlayedAt: null,
          };
        }
        console.error('Error fetching player stats:', error);
        return null;
      }

      return {
        userId: data.user_id,
        eloRating: data.elo_rating,
        gamesPlayed: data.games_played,
        wins: data.wins,
        losses: data.losses,
        draws: data.draws,
        winStreak: data.win_streak,
        bestWinStreak: data.best_win_streak,
        lastPlayedAt: data.last_played_at,
      };
    } catch (error) {
      console.error('Error in getPlayerStats:', error);
      return null;
    }
  }

  /**
   * Get current user's stats
   */
  async getMyStats(): Promise<PlayerStats | null> {
    const user = authService.getCurrentUser();
    if (!user) {
      return null;
    }
    return this.getPlayerStats(user.id);
  }

  /**
   * Calculate ELO change preview (pure function, no database)
   * Uses standard ELO formula with K-factor of 32
   */
  calculateEloChange(
    winnerElo: number,
    loserElo: number,
    isDraw: boolean = false
  ): EloChange {
    // Calculate expected scores
    const expectedWinner = 1.0 / (1.0 + Math.pow(10, (loserElo - winnerElo) / 400.0));
    const expectedLoser = 1.0 - expectedWinner;

    let winnerChange: number;
    let loserChange: number;

    if (isDraw) {
      // Both get 0.5 as actual score
      winnerChange = Math.round(K_FACTOR * (0.5 - expectedWinner));
      loserChange = Math.round(K_FACTOR * (0.5 - expectedLoser));
    } else {
      // Winner gets 1.0, loser gets 0.0
      winnerChange = Math.round(K_FACTOR * (1.0 - expectedWinner));
      loserChange = Math.round(K_FACTOR * (0.0 - expectedLoser));
    }

    return {
      winnerChange,
      loserChange,
      winnerNewElo: winnerElo + winnerChange,
      loserNewElo: loserElo + loserChange,
    };
  }

  /**
   * Get ELO history for a player
   */
  async getEloHistory(
    userId: string,
    limit: number = 10
  ): Promise<EloHistoryEntry[]> {
    if (!isSupabaseConfigured() || !supabase) {
      return [];
    }

    try {
      const { data, error } = await supabase
        .from('elo_history')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) {
        console.error('Error fetching ELO history:', error);
        return [];
      }

      return (data || []).map((row: any) => ({
        id: row.id,
        userId: row.user_id,
        gameId: row.game_id,
        oldElo: row.old_elo,
        newElo: row.new_elo,
        change: row.change,
        createdAt: row.created_at,
      }));
    } catch (error) {
      console.error('Error in getEloHistory:', error);
      return [];
    }
  }

  /**
   * Get rank badge based on ELO rating
   */
  getRankBadge(elo: number): RankBadge {
    for (const tier of RANK_TIERS) {
      if (tier.maxElo === null && elo >= tier.minElo) {
        return tier;
      }
      if (tier.maxElo !== null && elo >= tier.minElo && elo <= tier.maxElo) {
        return tier;
      }
    }
    // Default to Bronze if somehow no match
    return RANK_TIERS[0];
  }

  /**
   * Get all rank tiers for display
   */
  getRankTiers(): RankBadge[] {
    return [...RANK_TIERS];
  }

  /**
   * Calculate win rate percentage
   */
  calculateWinRate(wins: number, gamesPlayed: number): number {
    if (gamesPlayed === 0) return 0;
    return Math.round((wins / gamesPlayed) * 100);
  }

  /**
   * Report game result and update ratings
   * This calls the Supabase function to update both players
   */
  async reportGameResult(
    winnerId: string,
    loserId: string,
    gameId: string,
    isDraw: boolean = false
  ): Promise<boolean> {
    if (!isSupabaseConfigured() || !supabase) {
      return false;
    }

    try {
      const { error } = await supabase.rpc('update_player_ratings', {
        p_winner_id: winnerId,
        p_loser_id: loserId,
        p_game_id: gameId,
        p_is_draw: isDraw,
      });

      if (error) {
        console.error('Error reporting game result:', error);
        return false;
      }

      // Clear leaderboard cache so next fetch gets fresh data
      this.clearLeaderboardCache();
      
      return true;
    } catch (error) {
      console.error('Error in reportGameResult:', error);
      return false;
    }
  }
}

// Export singleton instance
export const leaderboardService = new LeaderboardService();
